/**
 * LearnHero — the `/learn` design.
 *
 * Verbatim render of `learn.html`. Modules in ./landing/learn/.
 * `wireLinks={true}` so PageHost handles:
 *   - `#/route`  → cleaned to `/route` and routed via React Router pushState
 *   - `#anchor`  → smooth-scroll to in-page section (#articles, #faq,
 *                  #glossary, #how, #resources, #start)
 *   - `https://` → opens normally (docs.t-rex.network, docs.erc3643.org,
 *                  erc3643.org/whitepaper, and the docs search query links)
 *
 * `onMount` populates the "From the journal" grid (`#lrnArticles`) with
 * the latest published articles fetched from `GET /api/articles?limit=3`.
 * The cards are built with the DOM API (createElement / textContent /
 * setAttribute) — never `innerHTML` — so user-controlled fields cannot
 * inject markup. The placeholder cards that ship with the file are only
 * overwritten after a successful fetch — on failure they remain so the
 * section is never empty.
 */
import { STYLES } from "./landing/learn/styles";
import { MARKUP } from "./landing/learn/markup";
import { SCRIPT } from "./landing/learn/script";
import { PageHost } from "./landing/PageHost";
import { api, mediaUrl } from "../lib/api";

function buildArticleCard(article) {
  const card = document.createElement("a");
  card.className = "ix-art";
  card.setAttribute("href", `/articles/${article.slug || ""}`);

  const imgWrap = document.createElement("div");
  imgWrap.className = "ix-art-img";
  if (article.cover_image) {
    const img = document.createElement("img");
    img.setAttribute("src", mediaUrl(article.cover_image));
    img.setAttribute("alt", article.title || "");
    img.setAttribute("loading", "lazy");
    imgWrap.appendChild(img);
  } else {
    const ph = document.createElement("span");
    ph.className = "ix-art-ph";
    ph.textContent = "T-REX";
    imgWrap.appendChild(ph);
  }
  card.appendChild(imgWrap);

  const body = document.createElement("div");
  body.className = "ix-art-body";

  const cat = document.createElement("span");
  cat.className = "ix-art-cat";
  cat.textContent = article.category || "Insight";
  body.appendChild(cat);

  const title = document.createElement("h3");
  title.className = "ix-art-title";
  title.textContent = article.card_title || article.title || "";
  body.appendChild(title);

  const excerpt = document.createElement("p");
  excerpt.className = "ix-art-ex";
  excerpt.textContent = article.card_excerpt || article.excerpt || "";
  body.appendChild(excerpt);

  card.appendChild(body);
  return card;
}

async function populateJournal(host) {
  const grid = host.querySelector("#lrnArticles");
  if (!grid) return;
  try {
    const res = await api.get("/articles", { params: { limit: 3 } });
    const articles = (res.data && res.data.articles) || [];
    if (articles.length === 0) return; // keep placeholder cards
    grid.replaceChildren(...articles.map(buildArticleCard));
  } catch (err) {
    // Non-fatal: network or 5xx → leave the placeholder cards in place.
    // We log so the failure is traceable in the browser console if an
    // admin is debugging an article that isn't appearing on /learn.
    console.warn("[learn] failed to fetch journal articles:", err);
  }
}

export function LearnHero() {
  return (
    <PageHost
      styles={STYLES}
      markup={MARKUP}
      script={SCRIPT}
      hostClass="trex-learn-host"
      wireLinks={true}
      onMount={populateJournal}
      data-testid="learn-hero"
    />
  );
}
